/**
 * DeliveryDate Custom Element
 *
 * A web component that handles delivery date selection with:
 * - Dynamic date validation based on location and time
 * - Integration with Flatpickr date picker
 */
class DeliveryDate extends HTMLElement {
  /**
   * Constructor sets up initial state
   */
  constructor() {
    super();

    // Internal state properties
    this.flatpickr = null;
  }

  /**
   * Component lifecycle method called when the element is added to the DOM
   */
  connectedCallback() {
    this.initializeState();
    this.loadDependencies();
    this.cacheElements();
    this.setupEventListeners();
    this.initializeUI();
  }

  /**
   * Initialize component state from attributes
   */
  initializeState() {
    // Parse giftcard-only attribute
    const giftcardAttr = this.getAttribute('data-giftcard-only');
    this.giftcardOnly = giftcardAttr === "true" || giftcardAttr === true;
  }

  /**
   * Load required external dependencies
   */
  loadDependencies() {
    // Load Flatpickr script dynamically if not already loaded
    if (!window.flatpickr) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/flatpickr/4.6.13/flatpickr.min.js';
      document.head.appendChild(script);
    }
  }


  /**
   * Cache DOM elements for better performance
   */
  cacheElements() {
    // Tab elements
    this.tabLinks = this.getElementsByClassName("tablink");
    this.contentBlocks = this.getElementsByClassName("tabcontent");

    // Shipping tab elements
    this.shippingTabContent = this.querySelector("#shipping_tabcontent");
    this.zipCodeInput = this.querySelector('#shipping_zipcode');
    this.locationIcon = this.querySelector( '#location_icon' );
    this.nextButton = this.querySelector( '#next_button' );
    this.drawerNextButton = this.querySelector( '#drawer_next_button' );
    this.shippingDeliveryDateClass = this.querySelector('#shipping_tabcontent .delivery-date');
    this.deliveryDate = this.querySelector('#delivery_date');
    this.deliveryDateInput = this.querySelector('#delivery_date__input');
    this.deliveryDateAlert = this.querySelector('#date_alert');
    this.warningMessage = this.querySelector('#warning_message');

    // Form attribute elements (for storing values)
    this.attributeDeliveryMethod = document.getElementById("attr_delivery_method");
    this.attributeDeliveryDate = document.getElementById("attr_delivery_date");
    this.attributeZipCode = document.getElementById("attr_zip_code");

    // Drawer attribute elements (for cart drawer)
    this.drawer = document.querySelector('cart-drawer.drawer');
    this.drawerAttributeDeliveryMethod = document.getElementById("drawer__attr_delivery_method");
    this.drawerAttributeDeliveryDate = document.getElementById("drawer__attr_delivery_date");
    this.drawerAttributeZipCode = document.getElementById("drawer__attr_zip_code");

    // Buttons
    this.submitButton = document.querySelector("#checkout");
    this.cartDrawerSubmitButton = document.querySelector( "#CartDrawer-Checkout" );
  }

  /**
   * Set up event listeners for user interactions
   */
  setupEventListeners () {
    // Input change events
    this.locationIcon.addEventListener('click', this.locationClicked.bind(this));
    this.zipCodeInput.addEventListener( 'input', this.zipCodeChanged.bind( this ) );
    if (this.nextButton) this.nextButton.addEventListener('click', this.nextButtonClicked.bind(this));
    if (this.drawerNextButton) this.drawerNextButton.addEventListener('click', this.nextButtonClicked.bind(this));

    this.deliveryDateInput.addEventListener('change', this.dateChanged.bind(this));
    this.deliveryDate.addEventListener('click', this.dateClicked.bind(this));
    this.deliveryDateInput.addEventListener('click', this.dateClicked.bind(this));
  }

  /**
   * Initialize the UI state
   */
  initializeUI() {
    // Start with shipping tab active
    // this.shippingTab.click();
    this.zipCodeChanged();
    // Show warning message when both ZIP code and date are empty
    if (this.zipCodeInput.value.length === 0 && this.deliveryDateInput.value.length === 0) {
      const warningMessage = getDateChangeWarningMessage();
      this.warningMessage.innerHTML = warningMessage || '';
    }
  }


  /**
   * Set focus on ZIP code input when location icon is clicked
   */
  locationClicked() {
    this.zipCodeInput.focus();
  }

  /**
   * Handle zip code changes and update available delivery dates
   */
  zipCodeChanged() {
    // Validate ZIP code (5 digits)
    const regex = /^\d{5}$/;
    const zipValid = this.zipCodeInput.value.length === 5 && regex.test(this.zipCodeInput.value);

    if (zipValid) {
      this.deliveryDateInput.disabled = false;

      this.cartVisibilityChanged(); // My new updates
      this.updateAttributeValues();

      // Check if ZIP code changed with a date already selected
      const zipChanged = this.zipCodeInput.value !== this.getDrawerZipCode() &&
                         this.deliveryDateInput.value.length === 10;

      if (zipChanged) {
        // Reset delivery date when ZIP changes
        this.deliveryDateInput.value = null;
      }

      // Initialize date picker with the new ZIP
      this.nextButton = this.querySelector( '#next_button' );
      this.drawerNextButton = this.querySelector( '#drawer_next_button' );
      if (this.nextButton) this.nextButton.addEventListener('click', this.nextButtonClicked.bind(this));
      if (this.drawerNextButton) this.drawerNextButton.addEventListener('click', this.nextButtonClicked.bind(this));


      if (this.nextButton) this.nextButton.disabled = false;
      if (this.drawerNextButton) this.drawerNextButton.disabled = false;
      this.initializeFlatpickr( null, this.zipCodeInput.value, this.giftcardOnly );
      this.deliveryDateAlert.style.visibility = "hidden";

      if ( this.deliveryDateInput.value.length !== 10 ) {
        const warningMessage = getDateChangeWarningMessage();
        this.warningMessage.innerHTML = warningMessage || '';
      }
    }
  }

  /**
   * Helper to get the current ZIP code from the drawer attribute
   * @returns {string} Current ZIP code
   */
  getDrawerZipCode() {
    return this.drawerAttributeZipCode ? this.drawerAttributeZipCode.value : '';
  }

  /**
   * Update attribute values based on current selections
   */
  updateAttributeValues() {
    // Update ZIP code attribute
    if (this.attributeZipCode) {
      this.attributeZipCode.value = this.zipCodeInput.value;
    }

    // Update drawer ZIP code attribute
    if (this.drawerAttributeZipCode) {
      this.drawerAttributeZipCode.value = this.zipCodeInput.value;
    }

    // Update delivery date attribute
    if (this.attributeDeliveryDate) {
      this.attributeDeliveryDate.value = this.deliveryDateInput.value;
    }

    // Update drawer delivery date attribute
    if (this.drawerAttributeDeliveryDate) {
      this.drawerAttributeDeliveryDate.value = this.deliveryDateInput.value;
    }
  }

  /**
   * Initialize the Flatpickr date picker with appropriate options
   * @param {string} pickupLocation - Pickup location or null for shipping
   * @param {string} shippingZipCode - ZIP code for delivery
   * @param {boolean} giftCardOnly - Whether this is a gift card only order
   */
  initializeFlatpickr(pickupLocation, shippingZipCode, giftCardOnly) {
    const deliveryMethod = pickupLocation ? "pickup" : "shipping";
    const dayOfWeekMap = {
      "sunday": 0, "monday": 1, "tuesday": 2, "wednesday": 3,
      "thursday": 4, "friday": 5, "saturday": 6
    };

    // Get excluded dates from backend function
    let disabledDates = getExcludedDates(pickupLocation, shippingZipCode, giftCardOnly);
    const minDate = disabledDates[0].end;
    const maxDate = disabledDates[1].start;

    // Parse disabled days of week
    const disabledDaysOfWeek = [];
    for (let i = 2; i < disabledDates.length; i++) {
      if (/^[A-Za-z]+$/.test(disabledDates[i])) {
        const day = disabledDates[i].toLowerCase();
        if (dayOfWeekMap[day] !== undefined) {
          disabledDaysOfWeek.push(dayOfWeekMap[day]);
        }
      }
    }

    let defaultDate = this.getAttribute( 'default-date' ) || null;
    if ( defaultDate && isDeliveryDay( defaultDate ) ) {
      if ( this.nextButton ) this.nextButton.click();
      if ( this.drawerNextButton ) this.drawerNextButton.click();
      this.warningMessage = this.querySelector('#warning_message');
      this.warningMessage.innerHTML = '';
    }
    else defaultDate = null;

    // Configure date picker
    const config = {
      dateFormat: this.getAttribute('date-format') || 'Y-m-d',
      altInput: true,
      altFormat: 'm/d/Y',
      defaultDate: defaultDate,
      wrap: true,
      mode: this.getAttribute('mode') || 'single',
      minDate: this.getAttribute('min-date') || minDate,
      maxDate: this.getAttribute('max-date') || maxDate,
      disable: [
        function(date) {
          // Check if day of week is disabled
          if (disabledDaysOfWeek.includes(date.getDay())) {
            return true;
          }

          // Check if specific date is disabled
          const dateString = date.toLocaleString('sv', { timeZone: 'America/New_York' }).substring(0, 10);
          for (let i = disabledDaysOfWeek.length + 2; i < disabledDates.length; i++) {
            if (dateString === disabledDates[i]) {
              return true;
            }
          }

          return false;
        }
      ],
      locale: {
        "firstDayOfWeek": 0
      }
    };

    // Initialize appropriate date picker based on delivery method
    if (deliveryMethod === "pickup") {
      this.initializePickupDatePicker(config);
    } else {
      this.initializeShippingDatePicker(config);
    }
  }

  /**
   * Initialize date picker for shipping
   * @param {object} config - Flatpickr configuration
   */
  initializeShippingDatePicker ( config ) {
    this.flatpickr = flatpickr(this.deliveryDate, config);

    // Add event listener for date selection
    this.deliveryDateInput.addEventListener('change', (e) => {
      this.dispatchDateChangedEvent();

      if (this.deliveryDateInput.value.length === 10 && this.zipCodeInput.value.length === 5) {
        this.updateDeliveryMethodAttributes("shipping");
        this.updateDeliveryDateAttributes(this.deliveryDateInput.value);
        this.updateZipCodeAttributes(this.zipCodeInput.value);
      }
    });
  }

  /**
   * Dispatch custom event when date changes
   */
  dispatchDateChangedEvent() {
    this.dispatchEvent(new CustomEvent('date-changed', {
      detail: {
        date: this.flatpickr.selectedDates,
        formatted: this.flatpickr.formatDate(this.flatpickr.selectedDates[0], this.flatpickr.config.dateFormat)
      }
    }));
    this.cartVisibilityChanged(); // My new updates
  }

  /**
   * Update delivery method attributes
   * @param {string} method - Delivery method (shipping or pickup)
   */
  updateDeliveryMethodAttributes(method) {
    if (this.drawerAttributeDeliveryMethod) {
      this.drawerAttributeDeliveryMethod.value = method;
    }

    if (this.attributeDeliveryMethod) {
      this.attributeDeliveryMethod.value = method;
    }
  }

  /**
   * Update delivery date attributes
   * @param {string} date - Selected delivery date
   */
  updateDeliveryDateAttributes(date) {
    if (this.drawerAttributeDeliveryDate) {
      this.drawerAttributeDeliveryDate.value = date;
    }

    if (this.attributeDeliveryDate) {
      this.attributeDeliveryDate.value = date;
    }
  }

  /**
   * Update ZIP code attributes
   * @param {string} zipCode - Selected ZIP code
   */
  updateZipCodeAttributes(zipCode) {
    if (this.drawerAttributeZipCode) {
      this.drawerAttributeZipCode.value = zipCode;
    }

    if (this.attributeZipCode) {
      this.attributeZipCode.value = zipCode;
    }
  }

  // This likely happens when the cart drawer is opened
  cartVisibilityChanged() {
    // Reread drawer elements from the DOM

    // Form attribute elements (for storing values)
    this.attributeDeliveryMethod = document.getElementById("attr_delivery_method");
    this.attributeDeliveryDate = document.getElementById("attr_delivery_date");
    this.attributeZipCode = document.getElementById("attr_zip_code");

    // Drawer attribute elements (for cart drawer)
    this.drawerAttributeDeliveryMethod = document.getElementById("drawer__attr_delivery_method");
    this.drawerAttributeDeliveryDate = document.getElementById("drawer__attr_delivery_date");
    this.drawerAttributeZipCode = document.getElementById("drawer__attr_zip_code");
  }


  /**
   * Activate shipping tab
   */
  activateShippingTab () {
    // Update UI
    this.shippingTabContent.style.display = "block";
    this.shippingTab.className += " active";

    // My debug code to see if the attributes are being updated
    // this.drawerAttributeDeliveryDate = document.getElementById("drawer__attr_delivery_date");
    // this.drawerAttributeZipCode = document.getElementById("drawer__attr_zip_code");

    // Update attributes
    this.updateDeliveryMethodAttributes("shipping");
    this.updateZipCodeAttributes(this.zipCodeInput.value);
    this.updateDeliveryDateAttributes(this.deliveryDateInput.value);
  }

  /**
   * Handle date changes
   * @param {Event} event - Change event
   */
  dateChanged ( event ) {
   if ( event.target.id === 'delivery_date__input' && this.zipCodeInput.value.length === 5 && this.deliveryDateInput.value.length === 10 ) {
      // Shipping, date selected, clear warning message;
      this.warningMessage.innerHTML = '';
      setCartAttributes( 'delivery-date', this.deliveryDateInput.value );
    }
    else if ( this.deliveryDateInput.value.length !== 10 ||
      ( event.target.id === 'delivery_date__input' && this.zipCodeInput.value.length !== 5 && this.deliveryDateInput.value.length === 10 ) ) {
      const warningMessage = getDateChangeWarningMessage();
      this.warningMessage.innerHTML = warningMessage || '';
    }
    else {
      // Update warning message if needed
      const warningMessage = getDateChangeWarningMessage();
      this.warningMessage.innerHTML = warningMessage || '';
      this.deliveryDateAlert.style.visibility = "hidden";
    }
  }

  nextButtonClicked( event ) {
    if ( this.zipCodeInput.value.length === 5 ) {
      if ( this.nextButton ) {
        this.nextButton.style.display = "none";
        this.nextButton.className = this.nextButton.className.replace("open", "closed");
      }
      if ( this.drawerNextButton ) {
        this.drawerNextButton.style.display = "none";
        this.drawerNextButton.className = this.drawerNextButton.className.replace( "open", "closed" );
      }
      setCartAttributes( 'delivery-zipcode', this.zipCodeInput.value );
      // this.deliveryDate.style.visibility = "visible";
      // this.shippingDeliveryDateClass.style.visibility = "visible";
      this.shippingDeliveryDateClass.style.display = "block";
      this.deliveryDateInput.focus();
    }
  }

  dateClicked( event ) {
    // Update warning message if needed
    if (this.zipCodeInput.value.length !== 5) {
      this.deliveryDateAlert.style.visibility = "visible";
      this.deliveryDateInput.disabled = true;
    }
    else {
      this.deliveryDateAlert.style.visibility = "hidden";
    }
  }
}

// Register the custom element
customElements.define('delivery-date', DeliveryDate);

async function setCartAttributes (key, value) {
  try {
    const attributes = {};
    attributes[key] = value;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const updatedCart = await response.json();
    return updatedCart;
  } catch (error) {
    console.error(`Error updating cart attribute ${key}:`, error);
    throw error; // Re-throw to allow further handling by caller
  }
}